const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface PickupPoint {
  id?: number;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  contactPerson: string;
  businessHours: string;
  notes: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  totalPrice: number;
  createdAt: string;
  updatedAt: string;
  numberOfBottles: number;
  countryCode: string;
  pickupPoint: any;
  deliveryAddress: any;
  customerInfo: any;
}

export interface DashboardStats {
  totalOrders: number;
  totalRevenue: number;
  pendingOrders: number;
  completedOrders: number;
  averageOrderValue: number;
}

export interface Country {
  id: number;
  code: string;
  name: string;
  currency: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PricingTier {
  id: string;
  countryId: string;
  quantity: number; // 1 to 50
  fixedPrice: number; // Total price for this quantity
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePricingTierRequest {
  countryCode: string;
  quantity: number; // 1 to 50
  fixedPrice: number; // Total price for this quantity
}

export interface UpdatePricingTierRequest {
  quantity?: number; // 1 to 50
  fixedPrice?: number; // Total price for this quantity
  isActive?: boolean;
}

export interface CountryPricing {
  country: Country;
  fixedPricing: PricingTier[];
}

export interface ApiResponse<T> {
  message: string;
  data: T;
  pagination?: {
    limit: number;
    offset: number;
    count: number;
  };
}

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }
    
    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(url, {
        headers,
        ...options,
      });

      // Handle 204 No Content responses
      if (response.status === 204) {
        return undefined as T;
      }

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          // Clear invalid token and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          throw new Error('Authentication failed. Please login again.');
        }
        
        // Try to get the error message from the response body
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          
          // Check if errorData has a message property
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Keep default error message if parsing fails
        }
        
        // Throw the error outside the try-catch
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Orders methods
  async getAllOrders(limit: number = 50, offset: number = 0): Promise<ApiResponse<Order[]>> {
    return this.request<ApiResponse<Order[]>>(`/orders?limit=${limit}&offset=${offset}`);
  }

  async getOrdersByStatus(status: string, limit: number = 50, offset: number = 0): Promise<ApiResponse<Order[]>> {
    return this.request<ApiResponse<Order[]>>(`/orders/status/${status}?limit=${limit}&offset=${offset}`);
  }

  async updateOrderStatus(orderId: string, status: string): Promise<ApiResponse<Order>> {
    return this.request<ApiResponse<Order>>(`/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async getOrderById(orderId: string): Promise<Order> {
    const response = await this.request<ApiResponse<Order>>(`/orders/${orderId}`);
    return response.data;
  }

  // Countries methods
  async getAllCountries(): Promise<Country[]> {
    return this.request<Country[]>('/pricing/countries');
  }

  // Pricing methods
  async getCountryPricing(countryCode: string): Promise<CountryPricing> {
    return this.request<CountryPricing>(`/pricing/countries/${countryCode}`);
  }

  async createPricingTier(data: CreatePricingTierRequest): Promise<ApiResponse<PricingTier>> {
    return this.request<ApiResponse<PricingTier>>('/pricing/fixed-pricing', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePricingTier(tierId: string, data: UpdatePricingTierRequest): Promise<ApiResponse<PricingTier>> {
    return this.request<ApiResponse<PricingTier>>(`/pricing/fixed-pricing/${tierId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePricingTier(tierId: string): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/pricing/fixed-pricing/${tierId}`, {
      method: 'DELETE',
    });
  }

  // Dashboard statistics
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Get all orders to calculate stats
      const allOrdersResponse = await this.getAllOrders(1000, 0);
      const orders = allOrdersResponse.data;

      // Calculate statistics
      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => {
        const price = typeof order.totalPrice === 'string' ? parseFloat(order.totalPrice) : order.totalPrice;
        return sum + (price || 0);
      }, 0);
      const pendingOrders = orders.filter(order => 
        ['pending', 'confirmed', 'processing'].includes(order.status)
      ).length;
      const completedOrders = orders.filter(order => 
        ['delivered'].includes(order.status)
      ).length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        totalOrders,
        totalRevenue,
        pendingOrders,
        completedOrders,
        averageOrderValue,
      };
    } catch (error) {
      console.error('Failed to get dashboard stats:', error);
      // Return default values if API fails
      return {
        totalOrders: 0,
        totalRevenue: 0,
        pendingOrders: 0,
        completedOrders: 0,
        averageOrderValue: 0,
      };
    }
  }

  // Pickup Point methods
  async searchPickupPoints(query: string, limit: number = 10): Promise<PickupPoint[]> {
    return this.request<PickupPoint[]>(`/pickup-points/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  async getAllPickupPoints(limit: number = 50, offset: number = 0, activeOnly: boolean = true): Promise<PickupPoint[]> {
    return this.request<PickupPoint[]>(`/pickup-points?limit=${limit}&offset=${offset}&active=${activeOnly}`);
  }

  async getPickupPointById(id: number): Promise<PickupPoint> {
    return this.request<PickupPoint>(`/pickup-points/${id}`);
  }

  async createPickupPoint(data: Omit<PickupPoint, 'id' | 'isActive' | 'createdAt' | 'updatedAt'>): Promise<PickupPoint> {
    return this.request<PickupPoint>('/pickup-points', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePickupPoint(id: number, data: Partial<PickupPoint>): Promise<PickupPoint> {
    return this.request<PickupPoint>(`/pickup-points/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePickupPoint(id: number): Promise<void> {
    await this.request<void>(`/pickup-points/${id}`, {
      method: 'DELETE',
    });
  }

  async deactivatePickupPoint(id: number): Promise<PickupPoint> {
    return this.request<PickupPoint>(`/pickup-points/${id}/deactivate`, {
      method: 'PATCH',
    });
  }

  async activatePickupPoint(id: number): Promise<PickupPoint> {
    return this.request<PickupPoint>(`/pickup-points/${id}/activate`, {
      method: 'PATCH',
    });
  }

  async findDuplicates(): Promise<PickupPoint[][]> {
    return this.request<PickupPoint[][]>('/pickup-points/duplicates/find');
  }

  // Country management methods
  async getAllCountriesForManagement(): Promise<Country[]> {
    const response = await this.request<ApiResponse<Country[]>>('/countries');
    return response.data;
  }

  async getActiveCountriesForManagement(): Promise<Country[]> {
    const response = await this.request<ApiResponse<Country[]>>('/countries/active');
    return response.data;
  }

  async getCountryByIdForManagement(id: number): Promise<Country> {
    const response = await this.request<ApiResponse<Country>>(`/countries/${id}`);
    return response.data;
  }

  async createCountryForManagement(data: { code: string; name: string; currency: string }): Promise<Country> {
    const response = await this.request<ApiResponse<Country>>('/countries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async updateCountryForManagement(id: number, data: Partial<Country>): Promise<Country> {
    const response = await this.request<ApiResponse<Country>>(`/countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async deleteCountryForManagement(id: number): Promise<void> {
    await this.request<void>(`/countries/${id}`, {
      method: 'DELETE',
    });
  }

  async deactivateCountryForManagement(id: number): Promise<Country> {
    const response = await this.request<ApiResponse<Country>>(`/countries/${id}/deactivate`, {
      method: 'PATCH',
    });
    return response.data;
  }

  async activateCountryForManagement(id: number): Promise<Country> {
    const response = await this.request<ApiResponse<Country>>(`/countries/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: true }),
    });
    return response.data;
  }

  async findCountryDuplicatesForManagement(): Promise<{ code: Country[]; name: Country[] }> {
    const response = await this.request<ApiResponse<{ code: Country[]; name: Country[] }>>('/countries/duplicates');
    return response.data;
  }

  // Temporary Orders management methods
  async cleanupExpiredTemporaryOrders(): Promise<{ message: string; deletedCount: number }> {
    return this.request<{ message: string; deletedCount: number }>('/temporary-orders/admin/cleanup', {
      method: 'DELETE',
    });
  }

  async getTemporaryOrderCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/temporary-orders/admin/count');
  }
}

export const apiService = new ApiService();
